import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Heart, Brain, Scale, Waves } from 'lucide-react';
import CTA from '../../components/CTA';

const BangorWeightManagement: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Weight Management Hypnotherapy Bangor | Sustainable Lifestyle Change</title>
        <meta
          name="description"
          content="Weight management hypnotherapy serving Bangor, County Down from a south Belfast clinic on the Ormeau Road. Sustainable wellness approach for age-related metabolism, menopause weight. CNHC registered."
        />
        <meta name="keywords" content="weight loss hypnotherapy Bangor, weight management Bangor County Down, menopause weight Bangor, mindful eating Bangor, emotional eating Bangor" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/bangor-weight-management/" />
        <meta property="og:title" content="Weight Management Hypnotherapy Bangor | Sustainable Lifestyle Change" />
        <meta property="og:description" content="Weight management hypnotherapy serving Bangor. Sustainable wellness approach for age-related metabolism, menopause, coastal lifestyle health." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Weight Management Bangor",
            "description": "CNHC registered weight management hypnotherapy serving Bangor, County Down. Specializing in age-related metabolism, menopause weight management, sustainable wellness approach.",
            "url": "https://www.sharonclare-hypnotherapy.com/bangor-weight-management/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Weight Management Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Bangor",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Down"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy Serving Bangor
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Sustainable wellness approach for age-related metabolism and coastal lifestyle health
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">South Belfast clinic</span> on the Ormeau Road • <span className="font-semibold">around 30 min drive</span> from Bangor
                </p>
              </div>
              <p className="text-gray-600 mt-2">Frequent trains on the Belfast-Bangor line • Online sessions available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy for Bangor and North Down
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I serve the Bangor and North Down community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, on the Ormeau Road in the south of the city, about 10 minutes from the city centre. The drive in from Bangor takes roughly half an hour, or you can take the frequent train service on the Belfast-Bangor line and finish the journey with a Metro bus along the Ormeau Road corridor or a short taxi. For Bangor residents who value coastal wellness and a holistic approach to health, weight management hypnotherapy offers evidence-based support that aligns with your lifestyle values.
              </p>
              <p>
                Weight management in your 50s, 60s, and beyond presents different challenges than weight loss in your 20s and 30s. Metabolism naturally slows with age, hormonal changes during menopause and perimenopause can trigger significant weight gain, medications may affect appetite and metabolism, and the life transitions of retirement, empty nest, or caring for aging parents can disrupt established routines and trigger emotional eating patterns.
              </p>
              <p>
                The wellness-focused culture in Bangor - with its coastal walks, active retirement communities, and health-conscious residents - creates the perfect environment for sustainable weight management. Hypnotherapy complements this by addressing the emotional and psychological aspects of eating that aren't resolved by diet and exercise alone. We work on stress management, emotional eating triggers, age-related metabolism acceptance, building sustainable habits that fit your lifestyle, and creating a compassionate relationship with your changing body.
              </p>
              <p>
                For Bangor clients, I also offer online sessions via video call. Many find online sessions convenient for maintaining consistency without the journey, particularly during winter months or when health concerns make travel challenging. The therapeutic effectiveness is equivalent to in-person sessions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Age-Related Weight Challenges */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Understanding Age-Related Weight Changes
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <Clock className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Metabolism Changes with Age
                </h3>
                <p className="text-gray-700">
                  From your 40s onwards, metabolism naturally slows by approximately 2-5% per decade. This isn't failure - it's normal biology. What worked for weight management in your 30s won't work in your 60s. Hypnotherapy helps you accept this reality without judgment while building sustainable habits appropriate for your current metabolism. We focus on working with your body rather than fighting against natural aging processes.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Heart className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Menopause and Perimenopause Weight Gain
                </h3>
                <p className="text-gray-700">
                  Hormonal changes during menopause and perimenopause can trigger significant weight gain, particularly around the abdomen. Declining estrogen affects fat distribution, insulin sensitivity, and mood - all of which impact eating behaviors. Many women experience increased emotional eating, sleep disruption (which affects hunger hormones), and frustration as previous weight management strategies stop working. Hypnotherapy addresses the emotional and stress-related aspects of this transition.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Brain className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Life Transitions and Emotional Eating
                </h3>
                <p className="text-gray-700">
                  Retirement, empty nest syndrome, caring for aging parents, bereavement, or health challenges in yourself or loved ones can all trigger emotional eating. Food becomes comfort during life transitions when routines change and stress increases. Hypnotherapy helps you develop healthier coping mechanisms during these transitions, addressing the emotional drivers of eating rather than just focusing on food choices.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Waves className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Sustainable Wellness Approach
                </h3>
                <p className="text-gray-700">
                  Crash diets and extreme exercise programs aren't appropriate or safe for older adults. The Bangor coastal lifestyle - walking, swimming, gentle activity - provides the perfect foundation for sustainable weight management. Hypnotherapy complements this by ensuring your eating behaviors support your wellness goals without the extremes that risk injury, nutrient deficiency, or rebound weight gain.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              How Hypnotherapy Supports Age-Related Weight Management
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Hypnotherapy addresses weight management at a subconscious level, which is where emotional eating patterns and stress responses are stored. As we age, these patterns often become more deeply entrenched, making them harder to change through willpower alone. The benefit of hypnotherapy is that it works with your subconscious mind, creating changes that feel natural rather than forced.
              </p>
              <p>
                For older adults, hypnotherapy is particularly effective because it's gentle, safe, and doesn't require physical exertion. You're simply sitting comfortably in a deeply relaxed state while we work on changing the subconscious patterns driving your eating behaviors. There's no risk of injury, no physical strain, and the relaxation itself has significant health benefits including reduced blood pressure, improved sleep, and lower stress levels.
              </p>
              <p>
                We focus on several key areas: accepting your changing metabolism without self-criticism, breaking emotional eating patterns that may have developed over decades, reconnecting with genuine hunger and fullness signals (which often become disconnected as we age), building sustainable habits that fit your current lifestyle and capabilities, and developing a compassionate relationship with your aging body rather than fighting against natural changes.
              </p>
              <p>
                Many Bangor clients in their 50s, 60s, and 70s find that addressing the emotional and psychological aspects of weight management finally creates the breakthrough they've been seeking. When you stop fighting your body and start working with it compassionately, weight management becomes far more achievable and sustainable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Bangor Clients Say About Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "After menopause, I gained 2 stone seemingly overnight and nothing I tried worked. Sharon's approach was completely different - accepting my changing metabolism while addressing the emotional eating that had crept in during this transition. I've lost 1.5 stone over 6 months and feel so much more at peace with my body."
                </p>
                <p className="font-semibold text-gray-900">M.T., Bangor - Menopause Weight Management</p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "At 68, I'd accepted that I'd always struggle with my weight. But Sharon showed me that age isn't the barrier - it's the emotional patterns I'd carried for decades. The online sessions were perfect for me, and I appreciated the gentle, respectful approach. I've lost a stone and feel healthier than I have in years."
                </p>
                <p className="font-semibold text-gray-900">P.K., Belfast - Retirement Weight Management</p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Empty nest syndrome hit hard and I was comfort eating constantly. The drive from Bangor gave me time to reflect, and Sharon's approach addressed the emotional side of eating I'd never dealt with. I've lost 2 stone over 8 months - slow and steady, which feels sustainable at my age (57)."
                </p>
                <p className="font-semibold text-gray-900">A.H., Bangor - Empty Nest Emotional Eating</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Bangor Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with menopause weight gain?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is particularly effective for menopause-related weight gain because it addresses the emotional and stress-related aspects of this transition. While hypnotherapy can't change your hormones, it can significantly reduce the emotional eating, sleep disruption, stress, and mood changes that contribute to menopause weight gain. By teaching your nervous system to stay calm, improving sleep quality (which affects hunger hormones), and breaking emotional eating patterns, many women find menopause weight management becomes far more achievable. The approach is gentle, safe, and works with your body rather than fighting against hormonal changes.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is hypnotherapy safe for older adults?
                </h3>
                <p className="text-gray-700">
                  Completely safe. Hypnotherapy simply involves sitting comfortably in a deeply relaxed state - there's no physical exertion, no risk of injury, and no side effects. In fact, the deep relaxation itself has significant health benefits for older adults including reduced blood pressure, improved sleep, lower stress levels, and enhanced overall wellbeing. With my 15 years of nursing experience, I'm attuned to the specific health considerations of older adults. Hypnotherapy is appropriate for people in their 60s, 70s, 80s, and beyond - age is not a barrier to the effectiveness of this approach.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Will I need to follow a strict diet or exercise program?
                </h3>
                <p className="text-gray-700">
                  No. I don't provide diet plans or prescribe exercise routines - I'm a hypnotherapist, not a dietitian or personal trainer. The focus is entirely on the psychological and emotional aspects of eating. If you're already working with a dietitian, nutritionist, or following medical dietary advice, hypnotherapy complements that support beautifully. If you're not, the changes we create at a subconscious level will naturally lead to better food choices and more mindful eating without needing rigid rules. The Bangor coastal lifestyle of walking and gentle activity provides excellent physical activity - hypnotherapy ensures your eating behaviors support your wellness goals.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can you help with emotional eating during life transitions?
                </h3>
                <p className="text-gray-700">
                  Yes, this is where hypnotherapy particularly excels. Life transitions - retirement, empty nest, bereavement, health challenges, caring for aging parents - often trigger emotional eating as we reach for food to cope with difficult emotions or fill the void left by changing circumstances. Hypnotherapy addresses these emotional patterns with compassion and understanding, teaching you healthier coping mechanisms and helping you navigate transitions without using food as your primary emotional support. Many Bangor clients find that addressing the emotional drivers of eating during life transitions creates lasting change that extends far beyond weight management.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvements within 4-8 sessions, with 4-6 being typical. This depends on how deeply entrenched your emotional eating patterns are, how much stress you're dealing with, and your specific goals. For older adults dealing with decades of established patterns, 6-8 sessions often provides more comprehensive change. We'll assess progress as we go and adjust accordingly. I offer a discounted 4-session package which many Bangor clients find provides excellent value. The time investment is modest compared to the lasting results - this isn't a quick fix, but it creates sustainable change.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is the journey from Bangor worthwhile?
                </h3>
                <p className="text-gray-700">
                  That's a personal decision, but many Bangor clients find the half-hour drive into Belfast becomes part of their self-care routine - time to reflect and transition into a therapeutic mindset. The frequent train service on the Belfast-Bangor line is also convenient. If travel feels challenging due to mobility, health concerns, or weather, online sessions provide exactly the same professional treatment without the journey. Consider that you're investing in sustainable, long-term weight management rather than another short-term diet. The modest time investment over a few months often creates lasting change that years of dieting hasn't achieved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default BangorWeightManagement;
